import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const emptyForm = {
  id: null,
  name: "",
  channel_type: "whatsapp",
  destination: "",
  branch_id: "",
  camera_id: "",
  detection_type: "",
  severity_min: "LOW",
  rules_json: {},
  start_time: "18:00",
  end_time: "00:00",
  timezone: "America/Argentina/Buenos_Aires",
  is_active: true,
};

const getApiBase = () => {
  const configured = process.env.REACT_APP_API_BASE;
  if (!configured) return "";
  return configured.replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const normalizeTime = (value) => String(value || "").slice(0, 5);

const NotificationContacts = () => {
  const theme = useTheme();
  const [contacts, setContacts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [history, setHistory] = useState([]);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/api/notification-contacts`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudieron cargar los contactos");
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  const fetchHistory = useCallback(async (cursor = null) => {
    setHistoryLoading(true);
    try {
      const suffix = cursor ? `?before_id=${encodeURIComponent(cursor)}` : "";
      const response = await fetch(`${getApiBase()}/api/saas/notifications/history${suffix}`, { headers: authHeaders() });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || "No se pudo cargar el historial");
      setHistory((previous) => cursor ? [...previous, ...(payload.deliveries || []).filter((row) => !previous.some((item) => item.id === row.id))] : (payload.deliveries || []));
      setHistoryCursor(payload.next_cursor || null);
      setHistoryError("");
    } catch (error) { setHistoryError(error.message); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchHistory();
    Promise.all([saasApi.getBranches(), saasApi.getCameras()])
      .then(([branchRows, cameraPayload]) => {
        setBranches(branchRows);
        setCameras(cameraPayload.cameras || []);
      })
      .catch((error) => notify(error.message || "No se pudieron cargar sucursales y cámaras", "error"));
  }, [fetchContacts, fetchHistory, notify]);

  const contactGroups = useMemo(() => {
    return contacts.reduce((acc, contact) => {
      const key = contact.name || "Sin nombre";
      acc[key] = acc[key] || [];
      acc[key].push(contact);
      return acc;
    }, {});
  }, [contacts]);

  const resetForm = () => setForm(emptyForm);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        start_time: normalizeTime(form.start_time),
        end_time: normalizeTime(form.end_time),
      };
      const isEdit = Boolean(form.id);
      const res = await fetch(`${getApiBase()}/api/notification-contacts${isEdit ? `/${form.id}` : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "No se pudo guardar el contacto");
      notify(isEdit ? "Contacto actualizado" : "Contacto creado");
      resetForm();
      fetchContacts();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const editContact = (contact) => {
    setForm({
      id: contact.id,
      name: contact.name || "",
      channel_type: contact.channel_type || "whatsapp",
      destination: contact.destination || "",
      branch_id: contact.branch_id || "",
      camera_id: contact.camera_id || "",
      detection_type: contact.detection_type || "",
      severity_min: contact.severity_min || "LOW",
      rules_json: typeof contact.rules_json === "string" ? JSON.parse(contact.rules_json || "{}") : (contact.rules_json || {}),
      start_time: normalizeTime(contact.start_time) || "18:00",
      end_time: normalizeTime(contact.end_time) || "00:00",
      timezone: contact.timezone || "America/Argentina/Buenos_Aires",
      is_active: contact.is_active !== 0,
    });
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Eliminar este canal de contacto?")) return;
    try {
      const res = await fetch(`${getApiBase()}/api/notification-contacts/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "No se pudo eliminar el contacto");
      notify("Contacto eliminado");
      fetchContacts();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  return (
    <Box m="20px">
      <Header
        title="CONTACTOS DE ALERTA"
        subtitle="Reglas por detector, severidad y horario para las alertas registradas. La entrega depende del canal configurado."
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 3,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h5">{form.id ? "Editar canal" : "Nuevo canal"}</Typography>

              <TextField
                label="Nombre del contacto"
                value={form.name}
                required
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select
                  label="Canal"
                  value={form.channel_type}
                  onChange={(event) => setForm((prev) => ({ ...prev, channel_type: event.target.value, destination: "" }))}
                >
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="push_web">Push web (preparado)</MenuItem>
                  <MenuItem value="sms">SMS (preparado)</MenuItem>
                  <MenuItem value="mobile_app">App movil (preparado)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={form.channel_type === "email" ? "Correo" : "Telefono WhatsApp"}
                value={form.destination}
                required
                placeholder={form.channel_type === "email" ? "claudio@empresa.com" : "5491121809844"}
                onChange={(event) => setForm((prev) => ({ ...prev, destination: event.target.value }))}
                fullWidth
              />

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Sucursal" value={form.branch_id} onChange={(event) => setForm((prev) => ({ ...prev, branch_id: event.target.value }))} fullWidth>
                    <MenuItem value="">Todas</MenuItem>
                    {branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Camara" value={form.camera_id} onChange={(event) => setForm((prev) => ({ ...prev, camera_id: event.target.value }))} fullWidth>
                    <MenuItem value="">Todas</MenuItem>
                    {cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.display_name || camera.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Deteccion" value={form.detection_type} onChange={(event) => setForm((prev) => ({ ...prev, detection_type: event.target.value }))} fullWidth placeholder="shoplifting, weapon..." />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Severidad minima" value={form.severity_min} onChange={(event) => setForm((prev) => ({ ...prev, severity_min: event.target.value }))} fullWidth>
                    {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((severity) => <MenuItem key={severity} value={severity}>{severity}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    label="Desde"
                    type="time"
                    value={form.start_time}
                    onChange={(event) => setForm((prev) => ({ ...prev, start_time: event.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Hasta"
                    type="time"
                    value={form.end_time}
                    onChange={(event) => setForm((prev) => ({ ...prev, end_time: event.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="00:00 equivale a 24hs"
                  />
                </Grid>
              </Grid>

              <TextField
                label="Zona horaria"
                value={form.timezone}
                onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
                fullWidth
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Activo</Typography>
                <Switch
                  checked={form.is_active}
                  onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
              </Stack>

              <Stack direction="row" gap={1}>
                <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving} fullWidth>
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
                {form.id && (
                  <Button variant="outlined" onClick={resetForm}>
                    Cancelar
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              minHeight: 520,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">Canales configurados</Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchContacts} disabled={loading}>
                Actualizar
              </Button>
            </Stack>

            {contacts.length === 0 ? (
              <Alert severity="info">Todavia no hay contactos configurados.</Alert>
            ) : (
              <Stack spacing={2}>
                {Object.entries(contactGroups).map(([name, group]) => (
                  <Box
                    key={name}
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: "divider",
                      background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.035)",
                    }}
                  >
                    <Typography variant="h6" mb={1}>{name}</Typography>
                    <Stack spacing={1}>
                      {group.map((contact) => (
                        <Stack
                          key={contact.id}
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          gap={1}
                        >
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              icon={contact.channel_type === "email" ? <EmailOutlinedIcon /> : <WhatsAppIcon />}
                              label={contact.channel_type === "email" ? "Email" : "WhatsApp"}
                              color={contact.channel_type === "email" ? "primary" : "success"}
                              variant="outlined"
                              size="small"
                            />
                            <Typography>{contact.destination}</Typography>
                            <Typography color="text.secondary" variant="body2">
                              {normalizeTime(contact.start_time)} a {normalizeTime(contact.end_time)}
                            </Typography>
                            <Chip label={`Min ${contact.severity_min || "LOW"}`} variant="outlined" size="small" />
                            {contact.detection_type && <Chip label={contact.detection_type} variant="outlined" size="small" />}
                            <Chip
                              label={contact.is_active === 0 ? "Inactivo" : "Activo"}
                              color={contact.is_active === 0 ? "default" : "success"}
                              size="small"
                            />
                          </Stack>

                          <Stack direction="row" gap={0.5}>
                            <Tooltip title="Editar">
                              <IconButton onClick={() => editContact(contact)}>
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton color="error" onClick={() => deleteContact(contact.id)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 3 }, mt: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={2}>
          <Box><Typography variant="h5">Historial de notificaciones</Typography><Typography variant="body2" color="text.secondary">PENDING indica registro pendiente; no confirma un envío.</Typography></Box>
          <Button variant="outlined" onClick={() => fetchHistory()} disabled={historyLoading}>Actualizar historial</Button>
        </Stack>
        {historyError && <Alert severity="error">{historyError}</Alert>}
        {!historyError && !historyLoading && history.length === 0 && <Alert severity="info">Sin entregas registradas.</Alert>}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label="Historial de notificaciones">
            <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Contacto</TableCell><TableCell>Detector</TableCell><TableCell>Canal</TableCell><TableCell>Estado</TableCell></TableRow></TableHead>
            <TableBody>{history.map((item) => <TableRow key={item.id}><TableCell>{item.created_at ? new Date(item.created_at).toLocaleString() : "—"}</TableCell><TableCell>{item.contact_name}</TableCell><TableCell>{item.detection_type || "Todos"}</TableCell><TableCell>{item.channel_type}</TableCell><TableCell><Chip size="small" label={item.status} color={item.status === "SENT" ? "success" : item.status === "FAILED" ? "error" : "default"} /></TableCell></TableRow>)}</TableBody>
          </Table>
        </TableContainer>
        {historyCursor && <Button sx={{ mt: 2 }} variant="text" onClick={() => fetchHistory(historyCursor)} disabled={historyLoading}>Ver más</Button>}
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationContacts;
